import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, Badge, Modal, Form, Row, Col, Alert, Table } from 'react-bootstrap';
import axios from 'axios';
import { User, UserListResponse, UsersImportPayload, UsersImportResponse } from '../types';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import DataTable, { Column } from '../components/DataTable';
import ActionIconButton from '../components/ActionIconButton';

const Users: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'admin' | 'analista' | 'usuario' | 'gerente'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [canApproveFilter, setCanApproveFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [sortBy, setSortBy] = useState<
    | 'id'
    | 'name'
    | 'email'
    | 'role'
    | 'is_active'
    | 'can_approve'
    | 'can_be_requester_approver'
    | 'can_access_purchase_tabs'
    | 'can_access_projects'
    | 'can_view_all_documents'
    | 'created_at'
  >('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers();
    } else if (currentUser) {
      setLoading(false);
      setInitialized(true);
    }
  }, [
    currentUser,
    currentPage,
    itemsPerPage,
    debouncedSearch,
    roleFilter,
    statusFilter,
    canApproveFilter,
    sortBy,
    sortDirection,
  ]);

  const fetchUsers = async () => {
    let keepLoadingForPageClamp = false;
    try {
      setLoading(true);
      const params: any = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        search: debouncedSearch.trim() || undefined,
        sort_by: sortBy,
        sort_order: sortDirection,
      };

      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter === 'active') params.is_active = true;
      if (statusFilter === 'inactive') params.is_active = false;
      if (canApproveFilter === 'yes') params.can_approve = true;
      if (canApproveFilter === 'no') params.can_approve = false;

      const response = await axios.get<UserListResponse>(`${API_URL}/api/users/search`, { params });
      const total = response.data.total ?? 0;
      const maxPage = Math.max(1, Math.ceil(total / itemsPerPage) || 1);

      // Filtro/remoção pode deixar currentPage além do total — evita lista vazia falsa
      if (total > 0 && currentPage > maxPage) {
        keepLoadingForPageClamp = true;
        setCurrentPage(maxPage);
        setTotalItems(total);
        return;
      }

      setUsers(response.data.items);
      setTotalItems(total);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      setUsers([]);
      setTotalItems(0);
    } finally {
      if (!keepLoadingForPageClamp) {
        setLoading(false);
        setInitialized(true);
      }
    }
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  const handleSort = (column: string) => {
    const target = column as typeof sortBy;
    if (sortBy === target) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(target);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleExportUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = `usuarios_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar usuários:', error);
      showAlert({
        title: 'Erro',
        message: 'Erro ao exportar usuários.',
        variant: 'danger',
      });
    }
  };

  const handleDelete = async (userId: number) => {
    const ok = await showConfirm({
      title: 'Desativar usuário',
      message: 'Tem certeza que deseja desativar este usuário?',
      variant: 'warning',
      confirmLabel: 'Desativar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao desativar usuário:', error);
      showAlert({
        title: 'Erro',
        message: 'Erro ao desativar usuário',
        variant: 'danger',
      });
    }
  };

  const handleReactivate = async (userId: number) => {
    const ok = await showConfirm({
      title: 'Reativar usuário',
      message: 'Tem certeza que deseja reativar este usuário?',
      confirmLabel: 'Reativar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      await axios.post(`${API_URL}/api/users/${userId}/reactivate`);
      fetchUsers();
      showAlert({
        title: 'Sucesso',
        message: 'Usuário reativado com sucesso!',
        variant: 'success',
      });
    } catch (error: any) {
      console.error('Erro ao reativar usuário:', error);
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao reativar usuário',
        variant: 'danger',
      });
    }
  };

  const handleResetPassword = async (userId: number, userName: string) => {
    const ok = await showConfirm({
      title: 'Resetar senha',
      message: `Tem certeza que deseja resetar a senha de ${userName}? Uma nova senha será gerada e enviada por email.`,
      confirmLabel: 'Resetar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      const response = await axios.post(`${API_URL}/api/users/${userId}/reset-password`);
      const data = response.data;

      if (data.email_sent) {
        showAlert({
          title: 'Senha resetada',
          message: 'Senha resetada com sucesso! Um email foi enviado ao usuário com a nova senha.',
          variant: 'success',
        });
      } else {
        showAlert({
          title: 'Atenção',
          message:
            'Não foi possível enviar o e-mail. A senha não foi alterada. Verifique as configurações SMTP e tente novamente.',
          variant: 'warning',
        });
      }
      fetchUsers();
    } catch (error: any) {
      console.error('Erro ao resetar senha:', error);
      showAlert({
        title: 'Erro',
        message:
          error.response?.data?.detail ||
          'Não foi possível resetar a senha. Verifique o SMTP e tente novamente.',
        variant: 'danger',
      });
    }
  };

  const columns = useMemo<Column<User>[]>(() => [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'name', label: 'Nome', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    {
      key: 'role',
      label: 'Função',
      sortable: true,
      render: (user) => (
        <Badge
          bg={
            user.role === 'admin'
              ? 'danger'
              : user.role === 'analista'
                ? 'info'
                : user.role === 'gerente'
                  ? 'warning'
                  : 'secondary'
          }
        >
          {user.role}
        </Badge>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      render: (user) => (
        <Badge bg={user.is_active ? 'success' : 'secondary'}>
          {user.is_active ? 'Ativo' : 'Inativo'}
        </Badge>
      ),
    },
    {
      key: 'can_approve',
      label: 'Pode Aprovar',
      sortable: true,
      render: (user) => (
        <Badge bg={user.can_approve ? 'info' : 'secondary'}>
          {user.can_approve ? 'Sim' : 'Não'}
        </Badge>
      ),
    },
    {
      key: 'can_be_requester_approver',
      label: 'Requisitante Aprovador',
      sortable: true,
      render: (user) => (
        <Badge bg={user.can_be_requester_approver ? 'warning' : 'secondary'}>
          {user.can_be_requester_approver ? 'Sim' : 'Não'}
        </Badge>
      ),
    },
    {
      key: 'can_access_purchase_tabs',
      label: 'Acesso Oportunidades',
      render: (user) => (
        <Badge bg={user.can_access_purchase_tabs ? 'primary' : 'secondary'}>
          {user.can_access_purchase_tabs ? 'Sim' : 'Não'}
        </Badge>
      ),
    },
    {
      key: 'can_access_projects',
      label: 'Área de Projetos',
      render: (user) => (
        <Badge bg={user.can_access_projects ? 'primary' : 'secondary'}>
          {user.can_access_projects ? 'Sim' : 'Não'}
        </Badge>
      ),
    },
    {
      key: 'can_view_all_documents',
      label: 'Ver Todos Documentos',
      sortable: true,
      render: (user) => (
        <Badge bg={user.can_view_all_documents ? 'primary' : 'secondary'}>
          {user.can_view_all_documents ? 'Sim' : 'Não'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Ações',
      render: (user) => (
        currentUser?.role === 'admin' ? (
          <div className="d-flex flex-wrap gap-1">
            <ActionIconButton
              icon="✏️"
              label={`Editar usuário ${user.name}`}
              variant="outline-primary"
              onClick={() => {
                setEditingUser(user);
                setShowModal(true);
              }}
            />
            {user.is_active ? (
              <>
                {currentUser?.can_reset_user_passwords !== false && (
                  <ActionIconButton
                    icon="🔑"
                    label={`Resetar senha de ${user.name}`}
                    variant="outline-warning"
                    onClick={() => handleResetPassword(user.id, user.name)}
                  />
                )}
                {currentUser?.can_deactivate_users !== false && (
                  <ActionIconButton
                    icon="🚫"
                    label={`Desativar usuário ${user.name}`}
                    variant="outline-danger"
                    onClick={() => handleDelete(user.id)}
                  />
                )}
              </>
            ) : (
              currentUser?.can_deactivate_users !== false ? (
                <ActionIconButton
                  icon="✅"
                  label={`Reativar usuário ${user.name}`}
                  variant="outline-success"
                  onClick={() => handleReactivate(user.id)}
                />
              ) : (
                <span className="text-muted">-</span>
              )
            )}
          </div>
        ) : (
          <span className="text-muted">-</span>
        )
      ),
    },
  ], [currentUser]);

  if (!initialized && loading) {
    return <div className="loading-spinner">Carregando...</div>;
  }

  if (currentUser?.role !== 'admin') {
    return <div>Acesso negado</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Usuários</h2>
        <div className="d-flex gap-2">
          {currentUser?.role === 'admin' && (
            <>
              <Button variant="outline-primary" onClick={handleExportUsers}>
                Exportar Usuários
              </Button>
              <Button variant="outline-secondary" onClick={() => setShowImportModal(true)}>
                Importar Usuários
              </Button>
              <Button variant="success" onClick={() => setShowModal(true)}>
                Novo Usuário
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col md={3}>
              <Form.Control
                type="text"
                placeholder="Buscar por nome ou email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Col>
            <Col md={2}>
              <Form.Select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value as any);
                  setCurrentPage(1);
                }}
              >
                <option value="all">Todas as funções</option>
                <option value="admin">Administrador</option>
                <option value="analista">Analista</option>
                <option value="gerente">Gerente</option>
                <option value="usuario">Usuário</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as any);
                  setCurrentPage(1);
                }}
              >
                <option value="all">Todos os status</option>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={canApproveFilter}
                onChange={(e) => {
                  setCanApproveFilter(e.target.value as any);
                  setCurrentPage(1);
                }}
              >
                <option value="all">Aprovação: todos</option>
                <option value="yes">Pode aprovar</option>
                <option value="no">Não aprova</option>
              </Form.Select>
            </Col>
          </Row>

          <DataTable<User>
            data={users}
            columns={columns}
            loading={loading}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            sortColumn={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            emptyMessage="Nenhum usuário encontrado"
          />
        </Card.Body>
      </Card>

      {currentUser?.role === 'admin' && (
        <UserModal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setEditingUser(null);
          }}
          user={editingUser}
          onSuccess={fetchUsers}
          canManageAdminFlags={!!currentUser?.can_manage_user_admin_flags}
        />
      )}
      {currentUser?.role === 'admin' && (
        <ImportUsersModal
          show={showImportModal}
          onHide={() => setShowImportModal(false)}
          onSuccess={fetchUsers}
        />
      )}
    </div>
  );
};

const UserModal: React.FC<{
  show: boolean;
  onHide: () => void;
  user: User | null;
  onSuccess: () => void;
  canManageAdminFlags?: boolean;
}> = ({ show, onHide, user, onSuccess, canManageAdminFlags = false }) => {
  const { showAlert, showConfirm } = useModal();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'analista' | 'usuario' | 'gerente'>('usuario');
  const [canApprove, setCanApprove] = useState(false);
  const [canBeRequesterApprover, setCanBeRequesterApprover] = useState(false);
  const [canAccessPurchaseTabs, setCanAccessPurchaseTabs] = useState(false);
  const [canAccessProjects, setCanAccessProjects] = useState(false);
  const [projectsAccessLevel, setProjectsAccessLevel] = useState<'np0' | 'np3'>('np0');
  const [canViewAllDocuments, setCanViewAllDocuments] = useState(false);
  const [canManageChanges, setCanManageChanges] = useState(false);
  const [canEditOwnChangeCabDeck, setCanEditOwnChangeCabDeck] = useState(false);
  const [canEditGroupPeerChangeCabDeck, setCanEditGroupPeerChangeCabDeck] = useState(false);
  const [canViewChangeSecurityAudit, setCanViewChangeSecurityAudit] = useState(false);
  const [canCreateUsers, setCanCreateUsers] = useState(false);
  const [canManageUserRoles, setCanManageUserRoles] = useState(false);
  const [canResetUserPasswords, setCanResetUserPasswords] = useState(false);
  const [canDeactivateUsers, setCanDeactivateUsers] = useState(false);
  const [canManageUserAdminFlags, setCanManageUserAdminFlags] = useState(false);
  const [canViewUserAudit, setCanViewUserAudit] = useState(false);
  const [canGrantAnyUserAdminFlags, setCanGrantAnyUserAdminFlags] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setRole(user.role);
      setCanApprove(user.can_approve || false);
      setCanBeRequesterApprover(!!user.can_be_requester_approver);
      setCanAccessPurchaseTabs(user.can_access_purchase_tabs || false);
      setCanAccessProjects(Boolean(user.can_access_projects));
      setProjectsAccessLevel(user.projects_access_level === 'np3' ? 'np3' : 'np0');
      setCanViewAllDocuments(user.can_view_all_documents || false);
      setCanManageChanges(!!user.can_manage_changes);
      setCanEditOwnChangeCabDeck(!!user.can_edit_own_change_cab_deck);
      setCanEditGroupPeerChangeCabDeck(!!user.can_edit_group_peer_change_cab_deck);
      setCanViewChangeSecurityAudit(!!user.can_view_change_security_audit);
      setCanCreateUsers(!!user.can_create_users);
      setCanManageUserRoles(!!user.can_manage_user_roles);
      setCanResetUserPasswords(!!user.can_reset_user_passwords);
      setCanDeactivateUsers(!!user.can_deactivate_users);
      setCanManageUserAdminFlags(!!user.can_manage_user_admin_flags);
      setCanViewUserAudit(!!user.can_view_user_audit);
      setCanGrantAnyUserAdminFlags(!!user.can_grant_any_user_admin_flags);
      setPassword('');
    } else {
      setName('');
      setEmail('');
      setPassword('');
      setRole('usuario');
      setCanApprove(false);
      setCanBeRequesterApprover(false);
      setCanAccessPurchaseTabs(false);
      setCanAccessProjects(false);
      setProjectsAccessLevel('np0');
      setCanViewAllDocuments(false);
      setCanManageChanges(false);
      setCanEditOwnChangeCabDeck(false);
      setCanViewChangeSecurityAudit(false);
      setCanCreateUsers(false);
      setCanManageUserRoles(false);
      setCanResetUserPasswords(false);
      setCanDeactivateUsers(false);
      setCanManageUserAdminFlags(false);
      setCanViewUserAudit(false);
      setCanGrantAnyUserAdminFlags(false);
    }
  }, [user]);

  const handleGrantAnyFlagChange = async (checked: boolean) => {
    if (checked) {
      const ok = await showConfirm({
        title: 'Atenção: poder elevado',
        message:
          'Esta flag permite conceder QUALQUER permissão de gestão de usuários, inclusive restaurar flags que a pessoa não tem mais. Isso é um poder sensível.',
        details:
          'Se esta flag for removida depois, somente outro administrador que ainda tenha "Pode conceder qualquer flag de gestão" poderá restaurá-la (e as demais flags). Deseja continuar?',
        variant: 'danger',
        confirmLabel: 'Sim, ativar',
        cancelLabel: 'Cancelar',
      });
      if (!ok) return;
      setCanGrantAnyUserAdminFlags(true);
      return;
    }

    const ok = await showConfirm({
      title: 'Remover poder de conceder qualquer flag?',
      message:
        'Ao desmarcar e salvar, esta pessoa deixa de poder restaurar flags que não possui.',
      details:
        'Só outro administrador que ainda tenha "Pode conceder qualquer flag de gestão" poderá devolver esta permissão. Deseja continuar?',
      variant: 'warning',
      confirmLabel: 'Sim, desmarcar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    setCanGrantAnyUserAdminFlags(false);
  };

  const handleRoleChange = (nextRole: 'admin' | 'analista' | 'usuario' | 'gerente') => {
    setRole(nextRole);
    if (nextRole !== 'admin') {
      // Fora de admin, flags de gestão não se aplicam
      setCanCreateUsers(false);
      setCanManageUserRoles(false);
      setCanResetUserPasswords(false);
      setCanDeactivateUsers(false);
      setCanManageUserAdminFlags(false);
      setCanViewUserAudit(false);
      setCanGrantAnyUserAdminFlags(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const mgmtFlags = canManageAdminFlags
      ? {
          can_create_users: canCreateUsers,
          can_manage_user_roles: canManageUserRoles,
          can_reset_user_passwords: canResetUserPasswords,
          can_deactivate_users: canDeactivateUsers,
          can_manage_user_admin_flags: canManageUserAdminFlags,
          can_view_user_audit: canViewUserAudit,
          can_grant_any_user_admin_flags: canGrantAnyUserAdminFlags,
        }
      : {};

    const saveTimeoutMs = 20000;

    try {
      if (user) {
        await axios.put(
          `${API_URL}/api/users/${user.id}`,
          {
            name,
            email,
            role,
            can_approve: canApprove,
            can_be_requester_approver: canBeRequesterApprover,
            can_access_purchase_tabs: canAccessPurchaseTabs,
            can_access_projects: canAccessProjects,
            projects_access_level: canAccessProjects ? projectsAccessLevel : null,
            can_view_all_documents: canViewAllDocuments,
            can_manage_changes: canManageChanges,
            can_edit_own_change_cab_deck: canEditOwnChangeCabDeck,
            can_edit_group_peer_change_cab_deck: canEditGroupPeerChangeCabDeck,
            can_view_change_security_audit: canViewChangeSecurityAudit,
            ...mgmtFlags,
          },
          { timeout: saveTimeoutMs }
        );
      } else {
        await axios.post(
          `${API_URL}/api/users`,
          {
            name,
            email,
            password: password || undefined,
            role,
            can_approve: canApprove,
            can_be_requester_approver: canBeRequesterApprover,
            can_access_purchase_tabs: canAccessPurchaseTabs,
            can_access_projects: canAccessProjects,
            projects_access_level: canAccessProjects ? projectsAccessLevel : null,
            can_view_all_documents: canViewAllDocuments,
            can_manage_changes: canManageChanges,
            can_edit_own_change_cab_deck: canEditOwnChangeCabDeck,
            can_edit_group_peer_change_cab_deck: canEditGroupPeerChangeCabDeck,
            can_view_change_security_audit: canViewChangeSecurityAudit,
            ...mgmtFlags,
          },
          { timeout: saveTimeoutMs }
        );
      }
      onSuccess();
      onHide();
    } catch (error: any) {
      console.error('Erro ao salvar usuário', error);
      const timedOut = error?.code === 'ECONNABORTED' || /timeout/i.test(String(error?.message || ''));
      showAlert({
        title: 'Erro ao salvar usuário',
        message: timedOut
          ? 'A operação demorou demais. Verifique se o backend está respondendo e tente novamente.'
          : error.response?.data?.detail || 'Erro ao salvar usuário',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>{user ? 'Editar Usuário' : 'Novo Usuário'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nome</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Função</Form.Label>
            <Form.Select
              value={role}
              onChange={(e) =>
                handleRoleChange(e.target.value as 'admin' | 'analista' | 'usuario' | 'gerente')
              }
            >
              <option value="usuario">Usuário</option>
              <option value="gerente">Gerente</option>
              <option value="analista">Analista</option>
              <option value="admin">Administrador</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Pode Aprovar Requisições"
              checked={canApprove}
              onChange={(e) => setCanApprove(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Usuários com esta permissão podem aprovar requisições de compras.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Pode ser requisitante e aprovador na mesma requisição"
              checked={canBeRequesterApprover}
              onChange={(e) => setCanBeRequesterApprover(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Somente administradores liberam esta opção. Quando ativa, a pessoa pode ser
              selecionada como aprovador (ex.: Gerente do Contrato) na própria requisição.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Acesso ao módulo de Oportunidades"
              checked={canAccessPurchaseTabs}
              onChange={(e) => setCanAccessPurchaseTabs(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Define se o usuário pode visualizar e acessar a aba de Oportunidades.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Acesso à Área de Projetos"
              checked={canAccessProjects}
              onChange={(e) => setCanAccessProjects(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Administradores sempre acessam. Demais perfis precisam desta flag.
            </Form.Text>
          </Form.Group>
          {canAccessProjects && (
            <Form.Group className="mb-3">
              <Form.Label>Nível de acesso em Projetos</Form.Label>
              <Form.Select
                value={projectsAccessLevel}
                onChange={(e) => setProjectsAccessLevel(e.target.value === 'np3' ? 'np3' : 'np0')}
              >
                <option value="np0">NP0</option>
                <option value="np3">NP3</option>
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Na aba de Documentos, pode visualizar todos os documentos"
              checked={canViewAllDocuments}
              onChange={(e) => setCanViewAllDocuments(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Quando desmarcado, o usuário verá apenas os documentos criados por ele.
            </Form.Text>
          </Form.Group>

          <hr />
          <div className="fw-semibold mb-2">Gestão de Mudanças</div>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Gestor de mudanças"
              checked={canManageChanges}
              onChange={(e) => setCanManageChanges(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Só administradores concedem. O gestor vê e configura todo o módulo de mudanças
              (cadastros, calendário, CAB, planos).
            </Form.Text>
          </Form.Group>
          <Alert variant="light" className="small py-2 mb-3">
            <strong>Apresentação CAB:</strong> quem <strong>criou</strong> ou é{' '}
            <strong>solicitante</strong> da mudança sempre pode ver e editar o deck (qualquer
            status), sem flag extra.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Pode editar Apresentação CAB de colegas do mesmo grupo"
              checked={canEditGroupPeerChangeCabDeck}
              onChange={(e) => setCanEditGroupPeerChangeCabDeck(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Usa os grupos do cadastro <strong>Grupos</strong> (SupportNect). Se o criador ou o
              solicitante da mudança compartilhar ao menos um grupo com este usuário, ele vê o botão
              e pode editar o deck. Não libera o modelo global. Administrador ou gestor podem
              conceder.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3 d-none">
            <Form.Check
              type="checkbox"
              label="Pode editar o deck CAB das mudanças que criou (legado)"
              checked={canEditOwnChangeCabDeck}
              onChange={(e) => setCanEditOwnChangeCabDeck(e.target.checked)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Pode ver a auditoria de mudanças"
              checked={canViewChangeSecurityAudit}
              onChange={(e) => setCanViewChangeSecurityAudit(e.target.checked)}
            />
            <Form.Text className="text-muted">
              Libera só a consulta dos logs do módulo (IP, usuário, evento). Não torna a pessoa
              gestora. Administrador ou gestor podem conceder.
            </Form.Text>
          </Form.Group>

          {canManageAdminFlags && (
            <>
              <hr />
              <p className="fw-semibold mb-2">Permissões de gestão de usuários</p>
              <Form.Group className="mb-2">
                <Form.Check
                  type="checkbox"
                  label="Pode criar usuários"
                  checked={canCreateUsers}
                  onChange={(e) => setCanCreateUsers(e.target.checked)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Check
                  type="checkbox"
                  label="Pode alterar função (role) de usuários"
                  checked={canManageUserRoles}
                  onChange={(e) => setCanManageUserRoles(e.target.checked)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Check
                  type="checkbox"
                  label="Pode resetar senhas"
                  checked={canResetUserPasswords}
                  onChange={(e) => setCanResetUserPasswords(e.target.checked)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Check
                  type="checkbox"
                  label="Pode desativar/reativar usuários"
                  checked={canDeactivateUsers}
                  onChange={(e) => setCanDeactivateUsers(e.target.checked)}
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Check
                  type="checkbox"
                  label="Pode gerenciar estas flags de admin"
                  checked={canManageUserAdminFlags}
                  onChange={(e) => setCanManageUserAdminFlags(e.target.checked)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Pode ver auditoria de usuários"
                  checked={canViewUserAudit}
                  onChange={(e) => setCanViewUserAudit(e.target.checked)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Pode conceder qualquer flag de gestão (restaurar permissões)"
                  checked={canGrantAnyUserAdminFlags}
                  onChange={(e) => {
                    void handleGrantAnyFlagChange(e.target.checked);
                  }}
                />
                <Form.Text className="text-muted">
                  Sem esta opção, a pessoa só pode dar as permissões que ela mesma já tem.
                  Com ela, pode restaurar qualquer permissão de gestão — inclusive as que
                  foram removidas. Os admins iniciais da lista já nascem com esta opção.
                </Form.Text>
              </Form.Group>
            </>
          )}

          {!user && (
            <Form.Group className="mb-3">
              <Form.Label>Senha (opcional - será gerada automaticamente se não informada)</Form.Label>
              <Form.Control
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Deixe em branco para gerar senha automática"
              />
              <Form.Text className="text-muted">
                Se não informada, uma senha aleatória será gerada e enviada por email ao usuário.
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancelar
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const ImportUsersModal: React.FC<{
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}> = ({ show, onHide, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [result, setResult] = useState<UsersImportResponse | null>(null);

  const handleClose = () => {
    setFile(null);
    setDryRun(true);
    setLoading(false);
    setError('');
    setSuccess('');
    setResult(null);
    onHide();
  };

  const handleImport = async () => {
    if (!file) {
      setError('Selecione um arquivo JSON.');
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    setResult(null);

    try {
      const text = await file.text();
      const payload = JSON.parse(text) as UsersImportPayload;
      if (!payload || !Array.isArray(payload.users)) {
        throw new Error('Arquivo inválido: esperado objeto com campo "users".');
      }

      const response = await axios.post<UsersImportResponse>(
        `${API_URL}/api/users/import`,
        payload,
        { params: { dry_run: dryRun } }
      );
      setResult(response.data);
      if (response.data.errors > 0) {
        setError(`Importação finalizada com ${response.data.errors} erro(s).`);
      }
      setSuccess(
        dryRun
          ? `Validação concluída: ${response.data.created} criação(ões), ${response.data.updated} atualização(ões).`
          : `Importação concluída: ${response.data.created} criado(s), ${response.data.updated} atualizado(s).`
      );

      if (!dryRun) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Erro ao importar usuários:', err);
      setError(err.response?.data?.detail || err.message || 'Falha ao importar usuários.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Importar Usuários</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Arquivo JSON</Form.Label>
          <Form.Control
            type="file"
            accept=".json"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] || null)}
            disabled={loading}
          />
          <Form.Text className="text-muted">
            Utilize o arquivo gerado por "Exportar Usuários".
          </Form.Text>
        </Form.Group>
        <Form.Check
          type="switch"
          id="users-dry-run"
          label="Executar validação sem gravar (dry_run)"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
          disabled={loading}
          className="mb-3"
        />

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {result && (
          <div style={{ maxHeight: 200, overflow: 'auto' }}>
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Ação</th>
                  <th>Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((item, idx) => (
                  <tr key={`${item.email}-${idx}`}>
                    <td>{item.email}</td>
                    <td>{item.action}</td>
                    <td>{item.message || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Fechar
        </Button>
        <Button variant="primary" onClick={handleImport} disabled={!file || loading}>
          {loading ? 'Importando...' : dryRun ? 'Validar' : 'Importar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default Users;







